import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    LOCALE_ID,
    signal,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FormFieldModule } from '@components/forms/form-field/form-field.module';
import { ResultNotificationComponent } from '@components/ui/result-notification/result-notification.component';
import { PASSWORD_REGEX } from '@constants/app.constant';
import { ChangePasswordRequest, ResetPasswordStep } from '@core/models/auth.model';
import { ButtonDirective } from '@directives/button.directive';
import { BaseResponse } from '@models/common.model';
import { ResetPasswordResolver } from '@models/resolver.model';
import { AuthService } from '@services/auth.service';
import { DialogService } from '@services/dialog.service';
import { ToastService } from '@services/toast.service';
import { ValidatorUtils } from '@utils/validator.util';
import { SvgIconComponent } from 'angular-svg-icon';
import { catchError, debounceTime, finalize, of, take } from 'rxjs';
import { messages } from '../dialogs/auth-dialog/message';

@Component({
    selector: 'pn-reset-password',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        SvgIconComponent,
        ButtonDirective,
        FormFieldModule,
        ResultNotificationComponent,
    ],
    templateUrl: './reset-password.component.html',
    styleUrl: './reset-password.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    
})
export class ResetPasswordComponent {
    public loading = signal<boolean>(false);
    public formGroup: FormGroup;
    public messages = messages;
    public passwordType = 'password';
    public rePasswordType = 'password';
    public resetPwStep = ResetPasswordStep.TokenValid;
    public resetPwStepEnum = ResetPasswordStep;
    private _token = '';

    constructor(
        @Inject(LOCALE_ID) protected readonly _localeId: string,
        private readonly _auth: AuthService,
        private readonly _fb: FormBuilder,
        private readonly _toast: ToastService,
        private readonly _dialog: DialogService,
        private readonly _route: ActivatedRoute,
        private readonly _router: Router,
        private readonly _cd: ChangeDetectorRef
    ) {
        this.formGroup = this._createForm();
        _route.data.subscribe((data) => {
            const check = data['checkToken'] as ResetPasswordResolver;
            if (check) {
                this._token = check.token;
                this.resetPwStep = check.verified
                    ? ResetPasswordStep.TokenValid
                    : ResetPasswordStep.TokenInvalid;
            }
        });
    }

    /**
     * Create form group
     *
     * @private
     * @return {*}  {FormGroup}
     * @memberof ResetPasswordComponent
     */
    private _createForm(): FormGroup {
        const pwValidators = Validators.compose([
            Validators.required,
            Validators.pattern(PASSWORD_REGEX),
        ]);
        return this._fb.group(
            {
                password: ['', pwValidators],
                rePassword: ['', pwValidators],
            },
            {
                validators: [ValidatorUtils.compare('password', 'rePassword')],
            }
        );
    }

    /**
     * Handle submit
     *
     * @return {*}  {void}
     * @memberof ResetPasswordComponent
     */
    public handleSubmit(): void {
        if (this.loading() || this.formGroup.invalid) {
            return;
        }

        const value = this.formGroup.value;
        const req: ChangePasswordRequest = {
            NewPassword: value.password,
            Token: this._token,
        };

        this.loading.set(true);
        this._auth
            .resetPassword(req)
            .pipe(
                debounceTime(300),
                catchError(() => of(new BaseResponse({ ID: -1 }))),
                finalize(() => this.loading.set(false))
            )
            .subscribe((res) => {
                if (res.ID > 0) {
                    this.resetPwStep = ResetPasswordStep.ResetPWSuccess;
                    this._cd.detectChanges();
                    return;
                }
                this._toast.error(
                    $localize`:@@auth.dialog.reset-password:Cập nhật mật khẩu`,
                    res?.Message ||
                        $localize`:@@auth.dialog.reset-password.toasts.reset-failed:Cập nhật mật khẩu không thành công!`
                );
            });
    }

    /**
     * Handle sign in
     *
     * @param {MouseEvent} event
     * @memberof ResetPasswordComponent
     */
    public handleSignIn(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this._auth
            .openSignInDialog()
            .pipe(take(1))
            .subscribe((result) => {
                if (!result) {
                    return;
                }

                this._router.navigate(['/']);
            });
    }
}
